/// <reference types="vite/client" />
/**
 * ConversionFunnelPanel — Phase 15 P15-8 (#50). The audit→conversion
 * funnel (sent → opened → clicked → replied → booked) with per-stage
 * drop-off, the milestone's success metric. Reads /api/conversion/funnel,
 * refetched on the realtime signals that move it.
 */

import { useCallback, useEffect, useState } from "react";
import { useAgentSocketContext } from "../hooks/useAgentSocketContext";

interface ConversionStage {
  key: string;
  label: string;
  count: number;
  drop_off_from_previous: number;
}

interface Funnel {
  days: number;
  engagement: string | null;
  stages: ConversionStage[];
}

export function ConversionFunnelPanel() {
  const { subscribe } = useAgentSocketContext();
  const [funnel, setFunnel] = useState<Funnel | null>(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/conversion/funnel?days=${days}`);
      if (res.ok) setFunnel((await res.json()) as Funnel);
    } catch {
      // best-effort — keep the last data on screen
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const refresh = () => { void load(); };
    const unsubs = [
      subscribe("growth.cta_clicked", refresh),
      subscribe("growth.meeting_booked", refresh),
      subscribe("marketing.sequence_advanced", refresh),
      subscribe("integrator.email_sent", refresh),
    ];
    return () => { unsubs.forEach((u) => u()); };
  }, [subscribe, load]);

  // Guard against a 200 body without `stages` (Phase-14 list-page bug class).
  const stages = funnel?.stages ?? [];
  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  return (
    <section className="space-y-4">
      <div className="section-rule flex items-center justify-between">
        <p className="eyebrow">// Tunnel de revenu — audit → RDV</p>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="font-mono text-xs px-2 py-1 rounded"
          style={{ background: "var(--color-bg-card)", color: "var(--color-text)", border: "1px solid var(--color-border)" }}
        >
          <option value={7}>7j</option>
          <option value={30}>30j</option>
          <option value={90}>90j</option>
        </select>
      </div>

      <div className="panel p-5">
        {stages.length > 0 ? (
          <div className="space-y-2">
            {stages.map((s, i) => {
              const widthPct = (s.count / maxCount) * 100;
              const convPct = Math.round((1 - s.drop_off_from_previous) * 100);
              return (
                <div key={s.key} className="space-y-1">
                  <div className="flex items-center justify-between font-mono text-xs">
                    <span className="text-[var(--color-text)]">{s.label}</span>
                    <span className="text-[var(--color-text-muted)] tabular-nums">
                      {s.count}
                      {i > 0 && <span className="ml-2 text-[var(--color-text-dim)]">{convPct}%</span>}
                    </span>
                  </div>
                  <div className="h-4 rounded" style={{ background: "var(--color-bg)" }}>
                    <div
                      className="h-full rounded transition-all"
                      style={{
                        width: `${widthPct}%`,
                        background: "linear-gradient(90deg, var(--color-accent-bright), var(--color-accent))",
                      }}
                    />
                  </div>
                </div>
              );
            })}
            <p className="mt-3 font-mono text-[10px] text-[var(--color-text-dim)]">
              ⓘ « Cliqué » = clic sur le CTA de réservation ; « RDV booké » = statut booked ou opportunité demo_scheduled.
            </p>
          </div>
        ) : (
          <p className="font-mono text-xs text-[var(--color-text-dim)] blink">Loading funnel…</p>
        )}
      </div>
    </section>
  );
}
